import { useState, useRef } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { NavHeader } from "@/components/layout/nav-header";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Upload, Film, X, Loader2, Trash2 } from "lucide-react";
import { formatFileSize } from "@/utils/video-compression-simple";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { getMultipartUploader } from "@/utils/multipart-upload";

interface PrivateRecording {
  id: number;
  title: string | null;
  fileName: string;
  storageKey: string;
  fileSize: string | null;
  recordingDate: string | null;
  uploadedBy: number;
  createdAt: string;
  uploaderDisplayName: string | null;
  uploaderEmail: string;
}

export default function PrivateRecordingsPage() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: recordings = [], isLoading } = useQuery<PrivateRecording[]>({
    queryKey: ["/api/admin/private-recordings"],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/private-recordings/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete recording");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/private-recordings"] });
      toast({ title: "Recording deleted" });
      setDeleteId(null);
    },
    onError: () => {
      toast({ title: "Failed to delete recording", variant: "destructive" });
    },
  });

  const handleUpload = async (file: File, title: string) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Create the recording record + get presigned URL
      const createRes = await fetch("/api/admin/private-recordings/presigned-upload-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          fileSize: file.size,
          title: title || undefined,
        }),
      });
      if (!createRes.ok) throw new Error("Failed to create recording");
      const { recordingId } = await createRes.json();

      // Step 2: Upload via multipart uploader
      const uploader = getMultipartUploader();
      await uploader.uploadFile(
        file,
        { type: "private-recording", recordingId },
        "", // no setlist for private recordings
        {
          onProgress: (progress) => setUploadProgress(progress),
        }
      );

      queryClient.invalidateQueries({ queryKey: ["/api/admin/private-recordings"] });
      toast({ title: "Upload complete" });
      setShowUploadModal(false);
    } catch (err: any) {
      toast({ title: err.message || "Upload failed", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  return (
    <div className="min-h-screen bg-clubhouse-black">
      <NavHeader />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-[28px] font-semibold text-white">Private Recordings</h1>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-white hover:bg-white/90 text-black rounded-full px-5 text-[14px] font-medium"
          >
            Upload new video
          </Button>
        </div>

        {/* Table card */}
        <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-white/30" />
            </div>
          ) : recordings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-white/30">
              <Film className="h-8 w-8 mb-3 opacity-40" />
              <p className="text-[15px]">No private recordings yet</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left text-[12px] font-medium text-white/30 uppercase tracking-wider px-5 py-3">
                        File Name
                      </th>
                      <th className="text-left text-[12px] font-medium text-white/30 uppercase tracking-wider px-5 py-3">
                        Date
                      </th>
                      <th className="text-left text-[12px] font-medium text-white/30 uppercase tracking-wider px-5 py-3">
                        Uploaded By
                      </th>
                      <th className="w-12 px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {recordings.map((rec) => (
                      <tr key={rec.id} onClick={() => navigate(`/admin/private-recordings/${rec.id}`)} className="border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] transition-colors cursor-pointer">
                        <td className="px-5 py-4">
                          <p className="text-[14px] text-white/80 truncate max-w-[300px]">{rec.title || rec.fileName}</p>
                          {rec.fileSize && (
                            <p className="text-[12px] text-white/25 mt-0.5">{formatFileSize(Number(rec.fileSize))}</p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-[14px] text-white/40">
                            {rec.createdAt ? format(new Date(rec.createdAt), "MMM d, yyyy") : "\u2014"}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-[14px] text-white/40">
                            {rec.uploaderDisplayName || rec.uploaderEmail}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteId(rec.id); }}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-white/[0.06] transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-white/[0.06]">
                {recordings.map((rec) => (
                  <div key={rec.id} onClick={() => navigate(`/admin/private-recordings/${rec.id}`)} className="px-4 py-4 flex items-start justify-between gap-3 cursor-pointer active:bg-white/[0.02]">
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] text-white/80 truncate">{rec.title || rec.fileName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[12px] text-white/30">
                          {rec.createdAt ? format(new Date(rec.createdAt), "MMM d, yyyy") : "\u2014"}
                        </p>
                        {rec.fileSize && (
                          <>
                            <span className="text-white/10">&middot;</span>
                            <p className="text-[12px] text-white/25">{formatFileSize(Number(rec.fileSize))}</p>
                          </>
                        )}
                      </div>
                      <p className="text-[12px] text-white/25 mt-0.5">
                        {rec.uploaderDisplayName || rec.uploaderEmail}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteId(rec.id); }}
                      className="h-8 w-8 rounded-full flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-white/[0.06] transition-colors shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upload modal */}
      <UploadPrivateRecordingModal
        isOpen={showUploadModal}
        onClose={() => !isUploading && setShowUploadModal(false)}
        onUpload={handleUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete recording</DialogTitle>
            <DialogDescription>
              This will permanently delete the recording file. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-white/40 hover:text-white">Cancel</Button>
            </DialogClose>
            <Button
              onClick={() => deleteId && handleDelete(deleteId)}
              disabled={deleteMutation.isPending}
              className="bg-red-500/90 hover:bg-red-500 text-white"
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Simple upload modal (no setlist, no booking reference)
function UploadPrivateRecordingModal({
  isOpen,
  onClose,
  onUpload,
  isUploading,
  uploadProgress,
}: {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, title: string) => void;
  isUploading: boolean;
  uploadProgress: number;
}) {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [projectTitle, setProjectTitle] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setVideoFile(null);
      setProjectTitle("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Upload video</DialogTitle>
          <DialogDescription>Upload a private recording.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
            disabled={isUploading}
          />

          {!videoFile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              disabled={isUploading}
              className={`w-full rounded-xl border-2 border-dashed p-8 flex flex-col items-center gap-3 transition-all cursor-pointer ${
                isDragging
                  ? "border-white/30 bg-white/[0.06]"
                  : "border-white/[0.08] bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="h-10 w-10 rounded-full bg-white/[0.06] flex items-center justify-center">
                <Upload className="h-5 w-5 text-white/40" />
              </div>
              <div className="text-center">
                <p className="text-[14px] text-white/60">
                  Drop your video here or{" "}
                  <span className="text-white/90 underline underline-offset-2">browse</span>
                </p>
                <p className="text-[12px] text-white/25 mt-1">MP4, MOV, WebM</p>
              </div>
            </button>
          ) : (
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0">
                <Film className="h-5 w-5 text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] text-white/80 truncate">{videoFile.name}</p>
                <p className="text-[12px] text-white/30">{formatFileSize(videoFile.size)}</p>
              </div>
              {!isUploading && (
                <button
                  onClick={() => setVideoFile(null)}
                  className="h-7 w-7 rounded-full flex items-center justify-center text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Project Title */}
          <div className="space-y-2">
            <label htmlFor="project-title" className="text-[14px] text-white/50">Project Title</label>
            <Input
              id="project-title"
              type="text"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              placeholder="Enter a title for this recording..."
              disabled={isUploading}
              className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 text-[14px]"
            />
          </div>

          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-white/50">Uploading...</span>
                <span className="text-white/70 tabular-nums">{Math.round(uploadProgress)}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={isUploading} className="text-white/40 hover:text-white">
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={() => videoFile && onUpload(videoFile, projectTitle)}
            disabled={isUploading || !videoFile}
            className="bg-white hover:bg-white/90 text-black"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
